import * as React from "react";
import { useContext, useMemo, useCallback } from "react";
import styled, { css, useTheme } from "styled-components";
import merge from "lodash/merge";
import Plot from "views/components/visualizations/plotly/AsyncPlot";
import { EVENT_COLOR, eventsDisplayName } from "views/logic/searchtypes/events/EventHandler";
import { ROOT_FONT_SIZE } from "theme/constants";
import getDefaultPlotYLayoutSettings from "views/components/visualizations/utils/getDefaultPlotYLayoutSettings";
import ChartColorContext from "./ChartColorContext";
import InteractiveContext from "../contexts/InteractiveContext";
import RenderCompletionCallback from "../widgets/RenderCompletionCallback";
const StyledPlot = styled(Plot)(
  ({ theme }) => css`
    .customPopover .popover-content {
      padding: 0;
    }

    .hoverlayer .hovertext {
      rect {
        fill: ${theme.colors.global.contentBackground} !important;
        opacity: 0.9 !important;
      }

      .name {
        fill: ${theme.colors.text.primary} !important;
      }

      path {
        stroke: ${theme.colors.global.contentBackground} !important;
      }
    }
  `
);
const nonInteractiveLayout = {
  yaxis: { fixedrange: true },
  xaxis: { fixedrange: true },
  hovermode: false
};
const style = { height: "100%", width: "100%" };
const config = { displayModeBar: false, doubleClick: false, responsive: true, showTips: false };
const usePlotLayout = (layout) => {
  const theme = useTheme();
  const interactive = useContext(InteractiveContext);
  const { colors } = useContext(ChartColorContext);
  return useMemo(() => {
    const fontSettings = {
      color: theme.colors.text.primary,
      size: ROOT_FONT_SIZE * Number(theme.fonts.size.small.replace(/rem|em/i, "")),
      family: theme.fonts.family.body
    };
    const defaultLayout = {
      shapes: [],
      autosize: true,
      showlegend: false,
      margin: {
        t: 10,
        l: 40,
        r: 10,
        b: 0,
        pad: 0
      },
      legend: {
        orientation: "h",
        font: fontSettings
      },
      hoverlabel: {
        namelength: -1
      },
      paper_bgcolor: "transparent",
      plot_bgcolor: "transparent",
      title: {
        font: fontSettings
      },
      yaxis: getDefaultPlotYLayoutSettings(theme),
      xaxis: {
        automargin: true,
        tickfont: fontSettings,
        title: {
          font: fontSettings
        }
      }
    };
    const plotLayout = merge({}, defaultLayout, layout);
    plotLayout.shapes = plotLayout.shapes.map((shape) => {
      var _a, _b;
      return {
        ...shape,
        line: { ...(_a = shape == null ? void 0 : shape.line) != null ? _a : {}, color: ((_b = shape == null ? void 0 : shape.line) == null ? void 0 : _b.color) || colors.get(eventsDisplayName, EVENT_COLOR) }
      };
    });
    return interactive ? plotLayout : merge({}, plotLayout, nonInteractiveLayout);
  }, [colors, interactive, layout, theme]);
};
const usePlotChartData = (chartData, setChartColor) => {
  const theme = useTheme();
  const { colors } = useContext(ChartColorContext);
  return useMemo(
    () => chartData.map((chart) => {
      if (setChartColor && colors) {
        const conf = setChartColor(chart, colors);
        conf.outsidetextfont = { color: theme.colors.text.primary };
        if ((chart == null ? void 0 : chart.name) === eventsDisplayName) {
          const eventColor = colors.get(eventsDisplayName, EVENT_COLOR);
          conf.marker = { color: eventColor, size: 5 };
        }
        if (conf.line || conf.marker) {
          return merge(chart, conf);
        }
        return chart;
      }
      return chart;
    }),
    [chartData, colors, setChartColor, theme.colors.text.primary]
  );
};
const GenericPlot = ({
  chartData,
  layout = {},
  setChartColor = void 0,
  onClickMarker = () => {
  },
  onHoverMarker = () => {
  },
  onUnhoverMarker = () => {
  },
  onZoom = () => {
  },
  onAfterPlot = () => {
  },
  onInitialized = () => {
  }
}) => {
  const interactive = useContext(InteractiveContext);
  const plotLayout = usePlotLayout(layout);
  const plotChartData = usePlotChartData(chartData, setChartColor);
  const onRenderComplete = useContext(RenderCompletionCallback);
  const _onRelayout = useCallback(
    (axis) => {
      if (!axis.autosize && axis["xaxis.range[0]"] && axis["xaxis.range[1]"]) {
        const from = axis["xaxis.range[0]"];
        const to = axis["xaxis.range[1]"];
        onZoom(from, to);
      }
    },
    [onZoom]
  );
  const _onHoverMarker = useCallback(
    (event) => {
      const { points } = event;
      onHoverMarker == null ? void 0 : onHoverMarker({
        positionX: points[0].bbox.x0,
        positionY: points[0].bbox.y0,
        x: points[0].x,
        y: points[0].y
      });
    },
    [onHoverMarker]
  );
  const _onMarkerClick = useCallback(
    (e) => {
      onClickMarker == null ? void 0 : onClickMarker(
        {
          x: e.points[0].x,
          y: e.points[0].y
        },
        e
      );
    },
    [onClickMarker]
  );
  const _onAfterPlot = useCallback(() => {
    onRenderComplete();
    onAfterPlot();
  }, [onRenderComplete, onAfterPlot]);
  return /* @__PURE__ */ React.createElement(
    StyledPlot,
    {
      data: plotChartData,
      useResizeHandler: true,
      layout: plotLayout,
      style,
      onAfterPlot: _onAfterPlot,
      onClick: interactive ? _onMarkerClick : () => false,
      onHover: _onHoverMarker,
      onUnhover: onUnhoverMarker,
      onRelayout: interactive ? _onRelayout : () => {
      },
      config,
      onInitialized
    }
  );
};
export default GenericPlot;
